import dayjs from 'dayjs';
import { OrderExpiryInput, getOrderExpiresAt } from '@/entities/order/lib/getOrderExpiresAt';
import { TrackedOrder } from '@/entities/order/lib/useTrackedOrders';

export const getIsRemoteOrderCompleted = (statusIdentifier: string) =>
    ['DONE', 'CANCELLED', 'NOT_DELIVERED'].includes(statusIdentifier);

type QueryResult = {
    data: OrderExpiryInput | undefined;
    isFetched: boolean;
};

type ComputeResult = {
    orders: TrackedOrder[];
    changed: boolean;
};

export const computeNextTrackedOrders = (
    trackedOrders: TrackedOrder[],
    queries: QueryResult[],
): ComputeResult => {
    if (queries.some((query) => !query.isFetched)) {
        return { orders: trackedOrders, changed: false };
    }

    const nextOrders: TrackedOrder[] = [];
    let changed = false;

    queries.forEach((query, index) => {
        // If API returned no data (loading, error, 404), keep the order.
        if (!query.data) {
            nextOrders.push(trackedOrders[index]);
            return;
        }

        const isRemoteOrderCompleted = getIsRemoteOrderCompleted(
            query.data.status?.definition?.identifier ?? '',
        );

        // Remove orders whose expiry time has passed.
        if (dayjs().isAfter(getOrderExpiresAt(query.data))) {
            changed = true;
            return;
        }

        // Update isCompleted flag if remote status changed.
        if (
            (!trackedOrders[index].isCompleted && isRemoteOrderCompleted) ||
            (trackedOrders[index].isCompleted && !isRemoteOrderCompleted)
        ) {
            nextOrders.push({ ...trackedOrders[index], isCompleted: isRemoteOrderCompleted });
            changed = true;
        } else {
            nextOrders.push(trackedOrders[index]);
        }
    });

    return { orders: nextOrders, changed };
};
